import { NewIdeaDialog } from "@/components/ideas/new-idea-dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DateTimePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { NewWorkItemDialog } from "@/components/work-items/new-work-item-dialog";
import { ApiError, api } from "@/lib/api";
import { isoToPragueWall, pragueWallToIso } from "@/lib/datetime";
import { getSocket } from "@/lib/socket";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  Bug,
  FileText,
  Lightbulb,
  Loader2,
  Play,
  Square,
  Timer,
  Wrench,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTimerContext } from "./timer-context";

/**
 * Floating Start / Stop timer widget + the QuickCreatePills
 * shortcut stack. Port of the Next `active-timer.tsx`.
 *
 * The running timer is the user's one open time entry in Hub;
 * `GET /api/time-entries/timer` reads it.
 */

type TimerState = {
  startedAt: string;
  projectId: string;
  projectName: string;
  workItemId: string;
  workItemTitle: string;
  description: string;
};

type RunningTimer = {
  start: string;
  projectId?: string | null;
  description?: string | null;
  /** Labels of the running entry's project and work item. */
  projectName?: string | null;
  workItemId?: string | null;
  workItemTitle?: string | null;
};

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function formatElapsed(ms: number): string {
  // Clamp to non-negative — a freshly-started timer's `startedAt` can
  // be a touch ahead of the browser clock; show 00:00 in that window.
  const totalSecs = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

type Project = { id: string; name: string };
type WorkItem = { id: string; title: string; globalId: number; state?: string };
type Overhead = { id: string; name: string; isDefault: boolean };

// Module-level caches — the widget lives in the persistent app
// layout, so caching projects / work-items across dialog opens
// avoids the dev-mode 2-3s refetch on every Start Timer.
let projectsPromise: Promise<Project[]> | null = null;
function loadProjects(): Promise<Project[]> {
  if (!projectsPromise) {
    projectsPromise = api
      .get<{ projects: Project[] }>("/api/projects")
      .then((data) => (Array.isArray(data.projects) ? data.projects : []))
      .catch(() => {
        projectsPromise = null;
        return [];
      });
  }
  return projectsPromise;
}

const workItemsCache = new Map<string, Promise<WorkItem[]>>();
function loadWorkItems(projectId: string): Promise<WorkItem[]> {
  let p = workItemsCache.get(projectId);
  if (!p) {
    p = api
      .get<WorkItem[]>(`/api/work-items?projectId=${projectId}`)
      .then((data) => (Array.isArray(data) ? data : []))
      .catch(() => {
        workItemsCache.delete(projectId);
        return [];
      });
    workItemsCache.set(projectId, p);
  }
  return p;
}

const overheadsCache = new Map<string, Promise<Overhead[]>>();
function loadOverheads(projectId: string): Promise<Overhead[]> {
  let p = overheadsCache.get(projectId);
  if (!p) {
    p = api
      .get<Overhead[]>(`/api/projects/${projectId}/overheads`)
      .then((data) => (Array.isArray(data) ? data : []))
      .catch(() => {
        overheadsCache.delete(projectId);
        return [];
      });
    overheadsCache.set(projectId, p);
  }
  return p;
}

/**
 * Drop cached project / work-item lists — callers that create a
 * work item elsewhere (e.g. `NewWorkItemDialog`) invalidate the
 * affected project so the Start Timer dialog sees the new ticket.
 */
export function invalidateTimerCaches(scope: "all" | { projectId: string } = "all"): void {
  if (scope === "all") {
    projectsPromise = null;
    workItemsCache.clear();
    overheadsCache.clear();
  } else {
    workItemsCache.delete(scope.projectId);
    overheadsCache.delete(scope.projectId);
  }
}

export function ActiveTimer() {
  const { t } = useTranslation();
  const [timer, setTimer] = useState<TimerState | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [showStart, setShowStart] = useState(false);
  const [showStop, setShowStop] = useState(false);

  const [projects, setProjects] = useState<Project[]>([]);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [overheads, setOverheads] = useState<Overhead[]>([]);
  const [selProject, setSelProject] = useState("");
  // Unified target picker — work items + overhead categories live
  // in one combobox. Values: `wi:<id>` / `oh:<id>` / "" (project
  // only, no work item, no overhead — the timer lands as `logged`
  // for triage). Same shape as `/time-entries/new`'s picker.
  const [selTarget, setSelTarget] = useState("");
  const [description, setDescription] = useState("");
  // Editable start of the RUNNING timer (Prague wall time, the shape
  // DateTimePicker speaks). Only the stop/edit dialog uses it — lets
  // the user fix a timer they forgot to start on time.
  const [startWall, setStartWall] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingWorkItemRef = useRef<string | null>(null);
  // Guards against overlapping timer polls: if a poll is still in
  // flight when the next interval tick (or a focus event) fires, skip
  // it. Without this, a slow/stalled `/api/time-entries/timer` lets
  // requests pile up — they exhaust the browser's per-host connection
  // slots and starve every other fetch (notably the layout loader),
  // which surfaces as "Could not load layout / Failed to fetch".
  const pollInFlightRef = useRef(false);

  function openStartDialog() {
    const ctx = getTimerContext();
    setDescription("");
    if (ctx.projectId) {
      // Always pre-fill from the page the user is on, never from a
      // previous dialog. When the project is unchanged the load
      // effect below won't re-run, so set the target here; when it
      // changes, the effect consumes the pending ref instead.
      const target = ctx.workItemId ? `wi:${ctx.workItemId}` : "";
      setSelTarget(target);
      pendingWorkItemRef.current = ctx.projectId !== selProject ? (ctx.workItemId ?? null) : null;
      setSelProject(ctx.projectId);
    } else {
      setSelProject("");
      setSelTarget("");
      pendingWorkItemRef.current = null;
    }
    setShowStart(true);
  }

  // Opening the stop dialog also pre-fills the project / target
  // pickers from the running timer, so the user can re-point it
  // before stopping (or just stop).
  function openStopDialog() {
    if (timer) {
      setSelProject(timer.projectId || "");
      pendingWorkItemRef.current = timer.workItemId || null;
      // Preselect the WI target when the running timer has one
      // resolved; the overhead path can't be re-derived from
      // running-timer state alone (we'd need overheadId on the
      // RunningTimer payload), so it falls through to empty
      // until the user picks again.
      setSelTarget(timer.workItemId ? `wi:${timer.workItemId}` : "");
      setDescription(timer.description || "");
      setStartWall(isoToPragueWall(timer.startedAt));
    }
    setShowStop(true);
  }

  // Re-read the running timer, e.g. one started or stopped in another tab.
  const refreshTimer = useCallback(async () => {
    // Skip if a previous read hasn't settled yet — never let timer
    // requests stack up (see `pollInFlightRef`). A burst of pushes or a
    // focus-during-reconnect can fire several in quick succession.
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      const data = await api.get<RunningTimer | null>("/api/time-entries/timer");
      setTimer((prev) => {
        // Stopped elsewhere → drop it here too.
        if (!data) return null;
        // Same timer still running — refresh the labels (they may
        // resolve later if Hub-side mirroring was lagging) but keep
        // the existing reference so React doesn't churn the tick
        // effect. Only spawn a new state object when something
        // user-visible changed.
        const next: TimerState = {
          startedAt: data.start,
          projectId: data.projectId ?? "",
          projectName: data.projectName ?? "",
          workItemId: data.workItemId ?? "",
          workItemTitle: data.workItemTitle ?? data.description ?? "",
          description: data.description ?? "",
        };
        if (
          prev &&
          prev.startedAt === next.startedAt &&
          prev.projectId === next.projectId &&
          prev.projectName === next.projectName &&
          prev.workItemId === next.workItemId &&
          prev.workItemTitle === next.workItemTitle &&
          prev.description === next.description
        ) {
          return prev;
        }
        return next;
      });
    } catch {
      // A network blip must not wipe the widget — leave state as-is.
    } finally {
      pollInFlightRef.current = false;
    }
  }, []);

  // On mount: read the timer once + prefetch projects. After that the
  // widget is PUSH-driven — no polling. The server pings this user's
  // tabs (`timer` event) whenever their timer starts, stops or is
  // re-pointed, so we re-read only on a real change. We also re-read on
  // tab focus and after a socket reconnect (to catch a ping missed while
  // offline).
  useEffect(() => {
    if (document.visibilityState === "visible") void refreshTimer();
    void loadProjects();

    const socket = getSocket();
    const onTimerPush = () => void refreshTimer();
    socket.on("timer", onTimerPush);
    socket.on("connect", onTimerPush);

    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshTimer();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      socket.off("timer", onTimerPush);
      socket.off("connect", onTimerPush);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshTimer]);

  // Tick.
  useEffect(() => {
    if (!timer) {
      setElapsed(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    const tick = () => {
      setElapsed(Date.now() - new Date(timer.startedAt).getTime());
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timer]);

  // Load projects when either dialog opens (served from cache) — the
  // stop dialog needs them for the re-point pickers too.
  useEffect(() => {
    if (!showStart && !showStop) return;
    let cancelled = false;
    setLoadingProjects(projects.length === 0);
    loadProjects()
      .then((data) => {
        if (!cancelled) setProjects(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showStart, showStop, projects.length]);

  // Load work items when a project is selected.
  useEffect(() => {
    if (!selProject) {
      setWorkItems([]);
      setOverheads([]);
      setSelTarget("");
      return;
    }
    let cancelled = false;
    const presetWorkItem = pendingWorkItemRef.current;
    pendingWorkItemRef.current = null;
    setSelTarget(presetWorkItem ? `wi:${presetWorkItem}` : "");
    setLoadingItems(true);
    Promise.all([loadWorkItems(selProject), loadOverheads(selProject)])
      .then(([wis, ovs]) => {
        if (cancelled) return;
        setWorkItems(wis);
        setOverheads(ovs);
      })
      .finally(() => {
        if (!cancelled) setLoadingItems(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selProject]);

  async function startTimer() {
    // Project is always required; the unified target picker resolves
    // to either a WI, an overhead category, or nothing (project-only
    // timer — lands as `logged` for triage).
    if (!selProject) return;
    const project = projects.find((p) => p.id === selProject);
    if (!project) return;
    const isWi = selTarget.startsWith("wi:");
    const isOh = selTarget.startsWith("oh:");
    const wiId = isWi ? selTarget.slice(3) : "";
    const ohId = isOh ? selTarget.slice(3) : "";
    const workItem = isWi ? workItems.find((w) => w.id === wiId) : undefined;
    const overhead = isOh ? overheads.find((o) => o.id === ohId) : undefined;

    setSaving(true);
    try {
      const data = await api.post<{ start: string }>("/api/time-entries/timer/start", {
        ...(workItem
          ? { workItemId: workItem.id }
          : overhead
            ? { overheadId: overhead.id }
            : { projectId: project.id }),
        description: description || undefined,
      });
      setTimer({
        startedAt: data.start,
        projectId: project.id,
        projectName: project.name,
        workItemId: workItem?.id ?? "",
        workItemTitle:
          workItem?.title ?? (overhead ? t("wiTimer.overheadTitle", { name: overhead.name }) : ""),
        description,
      });
      setShowStart(false);
      setSelProject("");
      setSelTarget("");
      setDescription("");
    } catch (err) {
      if (err instanceof ApiError) {
        const detail = err.detail as { error?: string; code?: string; detail?: string } | null;
        toast({
          title: t("wiTimer.startFailedStatus", { status: err.status }),
          description: [detail?.error, detail?.detail].filter(Boolean).join(" — ") || undefined,
          variant: "destructive",
        });
      } else {
        toast({ title: t("wiTimer.startFailed"), variant: "destructive" });
      }
    } finally {
      setSaving(false);
    }
  }

  // Re-point the running timer onto the picked project / WI /
  // overhead without stopping it.
  async function applyTimerChange() {
    if (!timer || !selProject) return;
    const project = projects.find((p) => p.id === selProject);
    if (!project) return;
    const isWi = selTarget.startsWith("wi:");
    const isOh = selTarget.startsWith("oh:");
    const wiId = isWi ? selTarget.slice(3) : "";
    const ohId = isOh ? selTarget.slice(3) : "";
    const workItem = isWi ? workItems.find((w) => w.id === wiId) : undefined;
    const overhead = isOh ? overheads.find((o) => o.id === ohId) : undefined;
    // Only send the start when the user actually moved it — the picker
    // is minute-resolution, so re-sending an unchanged value would
    // shave the original start's seconds off on every Apply.
    const origStartWall = timer ? isoToPragueWall(timer.startedAt) : "";
    const startChanged = startWall !== "" && startWall !== origStartWall;
    if (startWall !== "" && pragueWallToIso(startWall) === "") {
      toast({ title: t("wiTimer.invalidStart"), variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/time-entries/timer/update", {
        ...(workItem
          ? { workItemId: workItem.id }
          : overhead
            ? { overheadId: overhead.id }
            : { projectId: project.id }),
        description: description || undefined,
        ...(startChanged ? { start: pragueWallToIso(startWall) } : {}),
      });
      await refreshTimer();
      toast({ title: t("wiTimer.updated"), variant: "success" });
      setShowStop(false);
    } catch (err) {
      toast({
        title: t("wiTimer.updateFailed"),
        description: err instanceof ApiError ? String(err.status) : t("wiTimer.error"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  async function stopTimer() {
    if (!timer) return;
    setSaving(true);
    try {
      await api.post("/api/time-entries/timer/stop");
      setTimer(null);
      setShowStop(false);
    } catch {
      // keep dialog open on error
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* Outer wrapper is pointer-events-none so the empty stripe
          between the QuickCreatePills column and the timer button
          doesn't swallow clicks on whatever sits underneath (e.g.
          pagination buttons at the bottom of a list). Every
          interactive child re-enables pointer events on itself —
          QuickCreatePills does the same internally per-pill. */}
      <div className="pointer-events-none fixed right-4 bottom-4 z-50 flex flex-col items-end gap-2">
        <QuickCreatePills />

        {timer ? (
          <button
            type="button"
            onClick={openStopDialog}
            className="pointer-events-auto flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-foreground opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary-foreground" />
            </span>
            <span className="font-mono tabular-nums">{formatElapsed(elapsed)}</span>
            <Square className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={openStartDialog}
            aria-label={t("wiTimer.startAria")}
            className={cn(
              "pointer-events-auto group flex items-center rounded-full bg-card border shadow-lg",
              "h-11 w-11 hover:w-[150px] hover:px-4",
              "justify-center hover:justify-start",
              "overflow-hidden text-sm font-medium text-muted-foreground",
              "hover:bg-accent hover:text-accent-foreground transition-all duration-200",
            )}
          >
            <Timer className="h-5 w-5 shrink-0" />
            <span
              className={cn(
                "whitespace-nowrap ml-0 group-hover:ml-2",
                "max-w-0 opacity-0 overflow-hidden",
                "group-hover:max-w-[120px] group-hover:opacity-100",
                "transition-all duration-200",
              )}
            >
              {t("wiTimer.startTimer")}
            </span>
          </button>
        )}
      </div>

      <Dialog open={showStart} onOpenChange={setShowStart}>
        <DialogContent className="max-w-3xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-4 w-4 text-primary" />
              {t("wiTimer.startTimer")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div className="block">
              <span className="text-sm font-medium block mb-1.5">{t("wiTimer.project")}</span>
              <Combobox
                value={selProject}
                onChange={setSelProject}
                disabled={loadingProjects && projects.length === 0}
                loading={loadingProjects && projects.length === 0}
                placeholder={loadingProjects ? t("wiTimer.loading") : t("wiTimer.selectProject")}
                emptyLabel={t("wiTimer.noProjects")}
                options={projects.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>

            <div className="block">
              <span className="text-sm font-medium block mb-1.5">{t("wiTimer.target")}</span>
              <Combobox
                value={selTarget}
                onChange={setSelTarget}
                disabled={!selProject || (loadingItems && workItems.length === 0)}
                loading={loadingItems}
                placeholder={
                  !selProject
                    ? t("wiTimer.selectProjectFirst")
                    : loadingItems
                      ? t("wiTimer.loading")
                      : t("wiTimer.pickTarget")
                }
                emptyLabel={t("wiTimer.noOptions")}
                options={[
                  ...workItems.map((w) => ({
                    value: `wi:${w.id}`,
                    label: `#${w.globalId} ${w.title}`,
                    hint: t("wiTimer.hintWorkItem"),
                    struck: w.state === "closed",
                  })),
                  ...overheads.map((o) => ({
                    value: `oh:${o.id}`,
                    label: o.isDefault ? t("wiTimer.overheadDefault", { name: o.name }) : o.name,
                    hint: t("wiTimer.hintOverhead"),
                  })),
                ]}
              />
            </div>

            <label className="block">
              <span className="text-sm font-medium block mb-1.5">
                {t("wiTimer.descriptionOptional")}
              </span>
              <input
                type="text"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder={t("wiTimer.descriptionPlaceholder")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowStart(false)}>
              {t("wiTimer.cancel")}
            </Button>
            <Button
              size="sm"
              disabled={!selProject || saving}
              onClick={startTimer}
              className="gap-1.5"
            >
              <Play className="h-3.5 w-3.5" />
              {t("wiTimer.start")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showStop} onOpenChange={setShowStop}>
        <DialogContent className="max-w-3xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Square className="h-4 w-4 text-primary" />
              {t("wiTimer.stopTimer")}
            </DialogTitle>
          </DialogHeader>

          {timer && (
            <div className="space-y-3 py-1">
              <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                <span className="text-muted-foreground">{t("wiTimer.elapsed")}</span>
                <span className="font-mono font-medium">{formatElapsed(elapsed)}</span>
              </div>

              {/* Move the running timer's start — for when you forgot to
                  start it on time; the elapsed clock then recomputes. */}
              <div className="block">
                <span className="text-sm font-medium block mb-1.5">{t("wiTimer.startTime")}</span>
                <DateTimePicker
                  value={startWall}
                  onChange={setStartWall}
                  aria-label={t("wiTimer.timerStartAria")}
                />
              </div>

              {/* Re-point the running timer before stopping. */}
              <div className="block">
                <span className="text-sm font-medium block mb-1.5">{t("wiTimer.project")}</span>
                <Combobox
                  value={selProject}
                  onChange={setSelProject}
                  disabled={loadingProjects && projects.length === 0}
                  loading={loadingProjects && projects.length === 0}
                  placeholder={loadingProjects ? t("wiTimer.loading") : t("wiTimer.selectProject")}
                  emptyLabel={t("wiTimer.noProjects")}
                  options={projects.map((p) => ({ value: p.id, label: p.name }))}
                />
              </div>

              <div className="block">
                <span className="text-sm font-medium block mb-1.5">{t("wiTimer.target")}</span>
                <Combobox
                  value={selTarget}
                  onChange={setSelTarget}
                  disabled={!selProject || (loadingItems && workItems.length === 0)}
                  loading={loadingItems}
                  placeholder={
                    !selProject
                      ? t("wiTimer.selectProjectFirst")
                      : loadingItems
                        ? t("wiTimer.loading")
                        : t("wiTimer.pickTarget")
                  }
                  emptyLabel={t("wiTimer.noOptions")}
                  options={[
                    ...workItems.map((w) => ({
                      value: `wi:${w.id}`,
                      label: `#${w.globalId} ${w.title}`,
                      hint: t("wiTimer.hintWorkItem"),
                      struck: w.state === "closed",
                    })),
                    ...overheads.map((o) => ({
                      value: `oh:${o.id}`,
                      label: o.isDefault ? t("wiTimer.overheadDefault", { name: o.name }) : o.name,
                      hint: t("wiTimer.hintOverhead"),
                    })),
                  ]}
                />
              </div>

              <label className="block">
                <span className="text-sm font-medium block mb-1.5">
                  {t("wiTimer.descriptionOptional")}
                </span>
                <input
                  type="text"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder={t("wiTimer.descriptionPlaceholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={!selProject || saving}
                onClick={applyTimerChange}
              >
                <Wrench className="h-3.5 w-3.5 mr-1.5" />
                {t("wiTimer.apply")}
              </Button>

              <p className="text-xs text-muted-foreground">{t("wiTimer.stopHint")}</p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={stopTimer}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 mr-auto"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              {t("wiTimer.discard")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowStop(false)}>
              {t("wiTimer.keepRunning")}
            </Button>
            <Button size="sm" disabled={saving} onClick={stopTimer} className="gap-1.5">
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t("wiTimer.saving")}
                </>
              ) : (
                <>
                  <Square className="h-3.5 w-3.5" />
                  {t("wiTimer.stopSave")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Floating create-shortcut pills ─────────────────────────────────────
//
// Sits just above the Start-Timer button — the three most-used "new"
// actions plus "new idea", one click away from anywhere. Each pill
// opens an in-place dialog instead of navigating to a /new/* page.

const PILL_CLASSNAME = cn(
  // pointer-events-auto re-enables clicks on each individual pill —
  // the parent floating wrapper sets pointer-events-none so the
  // empty space between pills doesn't swallow clicks on the page
  // underneath (e.g. pagination buttons).
  "pointer-events-auto group flex items-center rounded-full bg-card border shadow-lg",
  "h-11 w-11 hover:w-[150px] hover:px-4",
  "justify-center hover:justify-start",
  "overflow-hidden text-sm font-medium text-muted-foreground",
  "hover:bg-accent hover:text-accent-foreground transition-all duration-200",
);

const PILL_LABEL_CLASSNAME = cn(
  "whitespace-nowrap ml-0 group-hover:ml-2",
  "max-w-0 opacity-0 overflow-hidden",
  "group-hover:max-w-[120px] group-hover:opacity-100",
  "transition-all duration-200",
);

function PillContent({ Icon, label }: { Icon: React.ElementType; label: string }) {
  return (
    <>
      <Icon className="h-5 w-5 shrink-0" />
      <span className={PILL_LABEL_CLASSNAME}>{label}</span>
    </>
  );
}

function QuickCreatePills() {
  const { t } = useTranslation();
  return (
    <>
      <NewWorkItemDialog type="feature">
        <button type="button" aria-label={t("wiTimer.newFeature")} className={PILL_CLASSNAME}>
          <PillContent Icon={FileText} label={t("wiTimer.newFeature")} />
        </button>
      </NewWorkItemDialog>
      <NewWorkItemDialog type="bug">
        <button type="button" aria-label={t("wiTimer.newBug")} className={PILL_CLASSNAME}>
          <PillContent Icon={Bug} label={t("wiTimer.newBug")} />
        </button>
      </NewWorkItemDialog>
      <NewWorkItemDialog type="standalone">
        <button type="button" aria-label={t("wiTimer.newTask")} className={PILL_CLASSNAME}>
          <PillContent Icon={Wrench} label={t("wiTimer.newTask")} />
        </button>
      </NewWorkItemDialog>
      <NewWorkItemDialog type="hotfix">
        <button type="button" aria-label={t("wiTimer.newHotfix")} className={PILL_CLASSNAME}>
          <PillContent Icon={AlertCircle} label={t("wiTimer.newHotfix")} />
        </button>
      </NewWorkItemDialog>
      <NewIdeaDialog>
        <button type="button" aria-label={t("wiTimer.newIdea")} className={PILL_CLASSNAME}>
          <PillContent Icon={Lightbulb} label={t("wiTimer.newIdea")} />
        </button>
      </NewIdeaDialog>
    </>
  );
}
